import { FC, Fragment, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { AnalyticsHooks } from '@frontend/analytics-shared';
import { theme } from '@frontend/theme';
import { Heading, Text, TextLink, useTooltip } from '@frontend/design-system';

export interface StatItem {
  label: string;
  value: string | number;
  onClick?: () => void;
  trackingId?: string;
  tooltip?: string;
  dataTestId?: string;
}

interface StatsListProps {
  items: StatItem[];
  styles?: Interpolation<Theme>;
}

export const StatsList: FC<StatsListProps> = ({ items, styles }) => {
  const isSmallScreen = AnalyticsHooks.useIsMobile();

  const { firstRowHighlightedItem, secondRowGroupedItems } = useMemo(() => {
    if (isSmallScreen && items.length > 2) {
      return {
        firstRowHighlightedItem: items[0],
        secondRowGroupedItems: items.slice(1),
      };
    }

    return {
      firstRowHighlightedItem: null,
      secondRowGroupedItems: items,
    };
  }, [isSmallScreen, items]);

  return (
    <div css={[defaultStyles.statsList, styles]}>
      {firstRowHighlightedItem && (
        <StatsItem {...firstRowHighlightedItem} isPrimary={true} styles={{ alignSelf: 'flex-start' }} />
      )}
      <div css={defaultStyles.statsGroupList}>
        {secondRowGroupedItems.map((item, index) => (
          <Fragment key={index}>
            <StatsItem {...item} isPrimary={!firstRowHighlightedItem && index === 0} />
            {index < secondRowGroupedItems.length - 1 && <div css={defaultStyles.divider} />}
          </Fragment>
        ))}
      </div>
    </div>
  );
};

interface StatsItemProps extends StatItem {
  isPrimary: boolean;
  styles?: Interpolation<Theme>;
  dataTestId?: string;
}
const StatsItem: FC<StatsItemProps> = ({
  label,
  value,
  onClick,
  trackingId,
  isPrimary,
  tooltip,
  styles,
  dataTestId,
}) => {
  const { tooltipProps, Tooltip, triggerProps } = useTooltip();
  return (
    <div css={[defaultStyles.statsItem, styles]} data-testid={dataTestId}>
      <Heading level={isPrimary ? 1 : 2} as='p' color={isPrimary ? 'default' : 'subdued'} css={{ lineHeight: 1 }}>
        {value}
      </Heading>
      {!!onClick ? (
        <TextLink size='small' onClick={onClick} textAlign='left' trackingId={trackingId} {...triggerProps}>
          {label}
        </TextLink>
      ) : (
        <Text size='small' {...triggerProps} textAlign='left'>
          {label}
        </Text>
      )}
      {!!tooltip && <Tooltip {...tooltipProps}>{tooltip}</Tooltip>}
    </div>
  );
};

const defaultStyles = {
  statsList: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  statsGroupList: css`
    display: flex;
    align-items: flex-end;
    gap: ${theme.spacing(3)};
  `,
  statsItem: css`
    display: flex;
    flex-direction: column;
  `,
  divider: css`
    height: 100%;
    border-left: 1px solid ${theme.colors.neutral20};
  `,
};
